/**
 * Processing overlay shown during dictation field extraction.
 *
 * Displays a large spinner in a light circle, bold heading, and
 * subtitle message. The form remains visible but faded behind.
 * @module features/voice-report/components/ProcessingOverlay/ProcessingOverlayView
 */

import React, { memo } from "react";
import { Box } from "@/components/atoms/Box";

import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/**
 * Props for ProcessingOverlayView
 */
interface ProcessingOverlayViewProps {
  /** Status message to display below the heading */
  readonly message: string;
}

/**
 * Full-screen overlay with spinner during dictation processing.
 * @param props - Component props
 * @param props.message - Processing status message
 */
const ProcessingOverlayView = ({ message }: ProcessingOverlayViewProps) => (
  <Box className="absolute inset-0 z-50 items-center justify-center bg-white dark:bg-surface-inverse">
    <Stack className="items-center gap-6 px-8">
      <Box className="size-24 items-center justify-center rounded-full bg-needs-semantic-primary-50">
        <Spinner size="large" className="text-needs-semantic-primary-500" />
      </Box>
      <Stack className="items-center gap-2">
        <Text variant="title-lg" className="text-black dark:text-white">
          Processing Your Dictation
        </Text>
        <Text
          variant="unstyled"
          className="text-center text-title-sm text-content-secondary dark:text-content-subtle"
        >
          {message || "Analyzing and pre-filling the report..."}
        </Text>
      </Stack>
    </Stack>
  </Box>
);

ProcessingOverlayView.displayName = "ProcessingOverlayView";

export default memo(ProcessingOverlayView);
